<template>
  <b-modal
    id="refNoWarningModal"
    ref="refNoWarningModal"
    :title="modalTitle"
    header-bg-variant="warning"
    header-text-variant="dark"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <div>
      <div class="mb-4">
        <!-- User-friendly error message -->
        <div
          v-if="errorMessage"
          class="alert alert-warning d-flex align-items-start mb-3"
        >
          <i class="fas fa-exclamation-triangle mr-2 mt-1"></i>
          <div>
            <div class="mb-2">{{ errorInstructions.userFriendlyMessage }}</div>
            <small class="text-muted">
              <i class="fas fa-code mr-1"></i>
              {{ errorMessage }}
            </small>
          </div>
        </div>

        <!-- Troubleshooting section -->
        <div class="alert alert-danger">
          <h6 class="mb-2"><i class="fas fa-tools mr-2"></i>วิธีแก้ไข:</h6>
          <ul class="mb-0 pl-3">
            <li
              v-for="instruction in errorInstructions.troubleshooting"
              :key="instruction"
            >
              {{ instruction }}
            </li>
          </ul>
        </div>

        <!-- Alternative section -->
        <div class="alert alert-info">
          <h6 class="mb-2">
            <i class="fas fa-lightbulb mr-2"></i>ทางเลือกอื่น:
          </h6>
          <ul class="mb-0 pl-3">
            <li
              v-for="instruction in errorInstructions.alternative"
              :key="instruction"
            >
              {{ instruction }}
            </li>
          </ul>
        </div>
      </div>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="handleCancel">
          ยกเลิก
        </b-button>
        <b-button 
          variant="warning" 
          class="mr-2" 
          @click="handleRetry"
          :disabled="isRetrying"
        >
          <i v-if="isRetrying" class="fas fa-spinner fa-spin mr-1"></i>
          <span v-if="!isRetrying">ลองใหม่</span>
          <span v-else>ลองใหม่ในอีก {{ retryCountdown }} วินาที</span>
        </b-button>
        <b-button variant="primary" @click="handleSubmit">
          พิมพ์โดยไม่ผ่าน EDC
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import slipSample from "../../assets/img/slipSample.jpg";
import { ERROR_MESSAGE_USER_REQUEST_PRINT_RECEIPT } from "@/constants";

export default {
  name: "RefWarningModal",
  props: {
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      slipSample,
      popoverConfig: {
        content: `<div class="p-2">
          <img src="${slipSample}" alt="ตัวอย่างใบเสร็จ" class="img-fluid mb-2" style="max-width: 200px"/>
        </div>`,
        html: true,
      },
      isRetrying: false,
      retryCountdown: 0,
      retryTimer: null,
    };
  },
  computed: {
    modalTitle() {
      return this.errorMessage === ERROR_MESSAGE_USER_REQUEST_PRINT_RECEIPT
        ? "ผู้ใช้ขอพิมพ์ใบเสร็จ"
        : "การเชื่อมต่อกับเครื่อง EDC ล้มเหลว";
    },
    errorInstructions() {
      const errorMappings = {
        "Failed to fetch": {
          userFriendlyMessage: "ไม่สามารถเชื่อมต่อกับ application EDC ได้",
          troubleshooting: [
            "ตรวจสอบว่าได้ติดตั้งโปรแกรม EDC แล้วหรือยัง",
            "ลองปิดแล้วเปิดโปรแกรม EDC ใหม่",
            "ตรวจสอบการทำงานของ Local Server",
          ],
          alternative: [
            "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
            "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
          ],
        },
        timeout: {
          userFriendlyMessage: "การเชื่อมต่อกับเครื่อง EDC ใช้เวลานานเกินไป",
          troubleshooting: [
            "ตรวจสอบการเชื่อมต่อเครื่อง EDC",
            "ลองเริ่มการทำงานใหม่อีกครั้ง",
          ],
          alternative: [
            "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
            "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
          ],
        },
        cancelled: {
          userFriendlyMessage: "การทำรายการถูกยกเลิกจากเครื่อง EDC",
          troubleshooting: [
            "ลองทำรายการใหม่อีกครั้ง",
            "ตรวจสอบการทำงานของเครื่อง EDC",
          ],
          alternative: [
            "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
            "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
          ],
        },
        "EDC busy, try again": {
          userFriendlyMessage: "เครื่อง EDC ไม่ว่างในขณะนี้",
          troubleshooting: [
            "รอสักครู่แล้วลองทำรายการใหม่อีกครั้ง",
            "ตรวจสอบว่าไม่มีรายการอื่นค้างอยู่บนเครื่อง EDC",
            "หากปัญหายังไม่หาย ให้ restart เครื่อง EDC",
          ],
          alternative: [
            "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
            "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
          ],
        },
        "Serial port unavailable": {
          userFriendlyMessage: "ไม่สามารถเชื่อมต่อกับเครื่อง EDC ได้",
          troubleshooting: [
            "ตรวจสอบ COM Port ใน Application เชื่อมต่อ",
            "ตรวจสอบสายเชื่อมต่อว่าเสียบเรียบร้อยแล้ว",
          ],
          alternative: [
            "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
            "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
          ],
        },
      };

      // Check for specific error patterns
      for (const [errorKey, config] of Object.entries(errorMappings)) {
        if (this.errorMessage && this.errorMessage.includes(errorKey)) {
          return config;
        }
      }

      // Default fallback
      return {
        userFriendlyMessage: "เกิดปัญหาในการเชื่อมต่อกับเครื่อง EDC",
        troubleshooting: [
          "ตรวจสอบการเชื่อมต่อเครื่อง EDC",
          "ลองทำรายการใหม่อีกครั้ง",
        ],
        alternative: [
          "กรุณาเลือกวิธีชำระและจำนวนเงินบนเครื่อง EDC โดยตรง",
          "เมื่อชำระเสร็จสามารถพิมพ์ใบเสร็จได้เลย",
        ],
      };
    },
  },
  methods: {
    show() {
      this.$refs.refNoWarningModal.show();
    },
    hide() {
      this.$refs.refNoWarningModal.hide();
      this.resetRetryState();
    },
    handleSubmit() {
      this.$emit("ref-confirm", { referenceId: "MANUAL" });
      this.hide();
    },
    handleCancel() {
      this.$emit("ref-cancel");
      this.hide();
    },
    handleRetry() {
      if (this.isRetrying) return;
      
      this.isRetrying = true;
      this.retryCountdown = 10;
      
      this.retryTimer = setInterval(() => {
        this.retryCountdown--;
        if (this.retryCountdown <= 0) {
          this.resetRetryState();
          this.$emit("ref-retry");
          this.hide();
        }
      }, 1000);
    },
    resetRetryState() {
      this.isRetrying = false;
      this.retryCountdown = 0;
      if (this.retryTimer) {
        clearInterval(this.retryTimer);
        this.retryTimer = null;
      }
    },
  },
};
</script>